"use client";

import { useCallback } from "react";
import { useAnalyticsContext, TrackingEvent, ProductType } from "@/analytics";
import { buildHotelAddToCartItem } from "@/analytics/services/hotel/utils";
import { getHotelListContext } from "@/analytics/services/hotel/hotelListContextStorage";

/**
 * Otel detay sayfasında "Odayı Ayır" (Seç) butonuna tıklanınca add_to_cart event'ini tetikler.
 *
 * dataLayer sırası:
 * 1. dataLayer.push({ ecommerce: null });
 * 2. dataLayer.push({ event: "ecom_event", event_category: "ecommerce", event_action: "add_to_cart", ecommerce: {...} });
 *
 * @param {Object} params
 * @param {Object} params.itemListId - Örn: "hotel_detail"
 * @param {string} params.itemListName - Örn: "Hotel Detail"
 * @param {string} [params.basketId] - Varsa gönderilir, yoksa parametre yollanmaz
 * @param {string} [params.crossProductPromotion]
 * @returns {{ trackAddToCart: (params: { product, roomOption, passengers }) => void }}
 */
export function useHotelDetailAddToCartAnalytics({
  itemListId = "hotel_detail",
  itemListName = "Hotel Detail",
  basketId,
  crossProductPromotion,
}) {
  const { dispatch: trackingDispatch } = useAnalyticsContext();

  const trackAddToCart = useCallback(
    ({ product, roomOption, passengers }) => {
      if (!product) return;

      const stored = getHotelListContext();
      const effectiveListId = stored?.item_list_id ?? itemListId;
      const effectiveListName = stored?.item_list_name ?? itemListName;

      const item = buildHotelAddToCartItem({
        product,
        roomOption,
        index: 0,
        item_list_id: effectiveListId,
        item_list_name: effectiveListName,
        cross_product_promotion: crossProductPromotion,
      });

      if (!item) return;

      const currency = item.currency ?? "TRY";
      // value: netPrice istenir. roomOption.price net; item.price gross kalabilir.
      const value =
        typeof roomOption?.price === "number"
          ? roomOption.price
          : typeof product?.price === "number"
            ? product.price
            : undefined;

      const ecommerce = {
        currency,
        ...(typeof value === "number" ? { value } : {}),
        ...(basketId != null && String(basketId).trim() !== "" ? { basket_id: String(basketId) } : {}),
        passengers,
        items: [item],
      };

      trackingDispatch({
        type: TrackingEvent.ADD_TO_CART,
        payload: {
          productType: ProductType.HOTEL,
          ecommerce,
        },
      });
    },
    [itemListId, itemListName, basketId, crossProductPromotion, trackingDispatch]
  );

  return { trackAddToCart };
}
